import { UserData, StudyPlanTask, StudyPlanData, WeeklyPlan } from '../UserAndProfile/UserContext';
import { getCurrentWeek, isStudyPlanStale } from '../StudyPlan/studyPlanUtils';
import { isStrategyCardPrompt } from '../../data/skillStrategyCards';

export type ChatMessage = {
  id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  source?: 'api' | 'strategy-card';
  canExpand?: boolean;
  isExpanded?: boolean;
  strategySkill?: string;
};

export type GenerationPhase = 'idle' | 'reading' | 'context' | 'drafting' | 'streaming';

export type StudyPlanNudgeData = {
  kind: 'goal-hit' | 'task-match' | 'review-task';
  task: StudyPlanTask;
  title: string;
  body: string;
};

export const createMessageId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const countWords = (text: string): number =>
  text.trim().split(/\s+/).filter(Boolean).length;

export const shouldShowExpandChip = (msg: ChatMessage): boolean => {
  if (msg.role !== 'assistant') return false;
  if (msg.source === 'strategy-card') return false;
  if (msg.isExpanded) return false;
  if (msg.canExpand === false) return false;
  if (!msg.content.trim()) return false;
  return countWords(msg.content) < 120;
};

export { isStrategyCardPrompt };

export const NUDGE_DISMISS_KEY = 'kai-study-plan-nudge-dismissed';

export function isNudgeDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(NUDGE_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissNudgeForSession(): void {
  try {
    sessionStorage.setItem(NUDGE_DISMISS_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearNudgeDismiss(): void {
  try {
    sessionStorage.removeItem(NUDGE_DISMISS_KEY);
  } catch {
    // ignore
  }
}

export function evaluateStudyPlanNudge(
  user: UserData | null,
  selectedSkill: string,
  options: { isMissedQuestionReview?: boolean; userMessageCount?: number } = {}
): StudyPlanNudgeData | null {
  if (!user?.studyPlan) return null;
  if (isStudyPlanStale(user.studyPlan)) return null;
  if (isNudgeDismissedThisSession()) return null;

  const currentWeek = getCurrentWeek(user.studyPlan, new Date());
  if (!currentWeek) return null;

  const { isMissedQuestionReview = false, userMessageCount = 0 } = options;

  if (isMissedQuestionReview) {
    const reviewTask = currentWeek.tasks.find(
      (t) => t.type === 'question-review' && !t.isCompleted
    );
    if (reviewTask) {
      return {
        kind: 'review-task',
        task: reviewTask,
        title: 'Review progress',
        body: `Mark "${reviewTask.title}" complete when you're done reviewing?`,
      };
    }
  }

  const skillsGoal = currentWeek.weeklySkillsPracticeQuestionGoal;
  const goalHit = skillsGoal?.target > 0 && skillsGoal.current >= skillsGoal.target;

  const skillTask = currentWeek.tasks.find(
    (t) =>
      t.type === 'skill-practice' &&
      !t.isCompleted &&
      t.skill &&
      (selectedSkill ? t.skill === selectedSkill : true)
  );

  if (goalHit && skillTask) {
    return {
      kind: 'goal-hit',
      task: skillTask,
      title: 'Weekly goal hit!',
      body: `You hit this week's practice goal (${skillsGoal.current}/${skillsGoal.target}). Mark "${skillTask.title}" complete?`,
    };
  }

  if (skillTask && selectedSkill && skillTask.skill === selectedSkill && userMessageCount >= 1) {
    return {
      kind: 'task-match',
      task: skillTask,
      title: 'On your study plan',
      body: `You're practicing ${selectedSkill} — that's on your plan. Mark "${skillTask.title}" complete when ready?`,
    };
  }

  if (goalHit) {
    const anyPending = currentWeek.tasks.find(
      (t) => t.type === 'skill-practice' && !t.isCompleted
    );
    if (anyPending) {
      return {
        kind: 'goal-hit',
        task: anyPending,
        title: 'Weekly goal hit!',
        body: `You hit this week's practice goal (${skillsGoal.current}/${skillsGoal.target}). Mark "${anyPending.title}" complete?`,
      };
    }
  }

  return null;
}

export function buildPersonalizePrompt(card: {
  headline: string;
  steps: string[];
  whenToUse: string;
  commonTrap?: string;
}): string {
  const steps = card.steps.map((s, i) => `${i + 1}. ${s}`).join('\n');
  const trap = card.commonTrap ? `\nCommon trap: ${card.commonTrap}` : '';
  return `Personalize this strategy for the current question. Apply it to this specific problem without giving the final answer unless I ask.\n\nStrategy: ${card.headline}\n${steps}\nWhen to use: ${card.whenToUse}${trap}`;
}

export function buildExpandPrompt(): string {
  return 'Expand your previous answer with 2–3 more concrete steps for this specific question. Do not repeat what you already said.';
}

/** Re-export types used by study plan helpers for convenience */
export type { StudyPlanData, WeeklyPlan, StudyPlanTask };
